import { useEffect, useState } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PercentIcon from '@mui/icons-material/Percent';
import GroupIcon from '@mui/icons-material/Group';
import BuildIcon from '@mui/icons-material/Build';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { API_URL, httpClient } from '../../../httpClient';
import {
  DashboardHeader,
  DateRangeFilter,
  isoToDateInputValue,
  type OverviewDashboardData,
  StatCard,
  toBrowserDayIsoRange,
  useDefaultDateRange,
} from './shared';

type DashboardTab = 'overview' | 'sourcing' | 'conversion' | 'retention' | 'operations';

interface NavCardProps {
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  target: DashboardTab;
  onNavigate?: (tab: DashboardTab) => void;
}

const NavCard = ({ label, description, icon: Icon, color, target, onNavigate }: NavCardProps) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${alpha(color, 0.3)}`,
        boxShadow: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.25)}`,
        },
      }}
    >
      <CardActionArea
        sx={{ height: '100%', p: 0 }}
        onClick={() => onNavigate?.(target)}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ p: 1.25, borderRadius: 2, backgroundColor: alpha(color, 0.15) }}>
              <Icon sx={{ fontSize: 24, color }} />
            </Box>
            <ArrowForwardIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const OverviewDashboard = ({
  onNavigate,
}: {
  onNavigate?: (tab: DashboardTab) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();

  const { ninetyDaysAgo, today } = useDefaultDateRange();
  const [startDate, setStartDate] = useState<string>(ninetyDaysAgo);
  const [endDate, setEndDate] = useState<string>(today);
  const [data, setData] = useState<OverviewDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Guard against a slow earlier response overwriting a newer one when the
    // date range changes rapidly (no AbortController → stale-response check).
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        const startIso = toBrowserDayIsoRange(startDate, false);
        const endIso = toBrowserDayIsoRange(endDate, true);
        if (startIso) queryParams.append('startDate', startIso);
        if (endIso) queryParams.append('endDate', endIso);

        const { json } = (await httpClient(
          `${API_URL}/job-application/dashboard/overview?${queryParams.toString()}`,
          { method: 'GET' },
        )) as { json: { data: OverviewDashboardData } };

        if (!cancelled) setData(json.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching overview dashboard:', error);
        notify('ra.notification.http_error', { type: 'error' });
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, notify]);

  return (
    <Box sx={{ p: 3 }}>
      <Title title={translate('resources.job-application-dashboard.dashboards.tabOverview')} />
      <DashboardHeader
        title={translate('resources.job-application-dashboard.dashboards.tabOverview')}
        subtitle={translate('resources.job-application-dashboard.subtitle')}
        dataSinceAt={data?.dataSinceAt}
      >
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          minDate={isoToDateInputValue(data?.dataSinceAt)}
        />
      </DashboardHeader>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.overview.newInPeriod')}
            value={data?.newInPeriod ?? 0}
            icon={PeopleIcon}
            color="#6366f1"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.overview.tooltips.newInPeriod')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.overview.hiredInPeriod')}
            value={data?.hiredInPeriod ?? 0}
            icon={CheckCircleIcon}
            color="#22c55e"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.overview.tooltips.hiredInPeriod')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.overview.activeNow')}
            value={data?.activeNow ?? 0}
            icon={TrendingUpIcon}
            color="#06b6d4"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.overview.tooltips.activeNow')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.overview.turnoverInPeriod')}
            value={data?.turnoverInPeriod ?? 0}
            icon={PercentIcon}
            color="#ef4444"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.overview.tooltips.turnoverInPeriod')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.overview.stuckOver7Days')}
            value={data?.stuckOver7Days ?? 0}
            icon={HourglassEmptyIcon}
            color="#f59e0b"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.overview.tooltips.stuckOver7Days')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.overview.totalInPipeline')}
            value={data?.totalInPipeline ?? 0}
            icon={CalendarMonthIcon}
            color="#8b5cf6"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.overview.tooltips.totalInPipeline')}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {translate('resources.job-application-dashboard.dashboards.title')}
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <NavCard
              label={translate('resources.job-application-dashboard.dashboards.tabSourcing')}
              description={translate('resources.job-application-dashboard.dashboards.sourcing.total')}
              icon={PeopleIcon}
              color="#6366f1"
              target="sourcing"
              onNavigate={onNavigate}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <NavCard
              label={translate('resources.job-application-dashboard.dashboards.tabConversion')}
              description={translate('resources.job-application-dashboard.dashboards.conversion.funnel')}
              icon={TrendingUpIcon}
              color="#22c55e"
              target="conversion"
              onNavigate={onNavigate}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <NavCard
              label={translate('resources.job-application-dashboard.dashboards.tabRetention')}
              description={translate('resources.job-application-dashboard.dashboards.retention.cohort')}
              icon={GroupIcon}
              color="#f59e0b"
              target="retention"
              onNavigate={onNavigate}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <NavCard
              label={translate('resources.job-application-dashboard.dashboards.tabOperations')}
              description={translate('resources.job-application-dashboard.dashboards.operations.aging')}
              icon={BuildIcon}
              color="#ef4444"
              target="operations"
              onNavigate={onNavigate}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
